import { useId } from 'react';

import { paths } from '@farmersdog/constants';
import { ResponsiveText } from '@farmersdog/corgi-legacy';
import { Box, Link } from '@farmersdog/corgi-x';

import config from '../../config';

import { CTA, Description, Title } from './body';
import styles from './EmailFormSlice.css';
import { useEmailForm } from './hooks/useEmailForm';
import { EmailInput, FirstNameInput, PetsCountInput, ZipInput } from './inputs';
import { VALIDATION } from './validation';

import type { EmailFormSliceType } from './types';

export interface EmailFormSliceProps {
  slice: EmailFormSliceType;
}

const websiteUrl = config.get('app.websiteUrl');

export function EmailFormSlice({ slice }: EmailFormSliceProps) {
  const { formTitle, formDesc, formCta } = slice.primary;

  const {
    formValues,
    validationMessages,
    completed,
    handleChange,
    handleBlur,
    handleSubmit,
    submitting,
    submitError,
  } = useEmailForm();

  const id = useId();

  return (
    <Box className={styles.container}>
      <Title id={id}>{formTitle}</Title>
      <Description>{formDesc}</Description>
      <form
        role="form"
        aria-labelledby={id}
        onSubmit={handleSubmit}
        className={styles.form}
      >
        <FirstNameInput value={formValues.firstName} onChange={handleChange} />
        <PetsCountInput value={formValues.petsCount} onChange={handleChange} />
        <ZipInput
          value={formValues.zip}
          onChange={handleChange}
          onBlur={handleBlur}
          validationMessage={validationMessages.zip}
          pattern={VALIDATION.zip.pattern.value}
        />
        <EmailInput
          value={formValues.email}
          onChange={handleChange}
          onBlur={handleBlur}
          validationMessage={validationMessages.email}
          pattern={VALIDATION.email.pattern.value}
        />
        <CTA disabled={!completed} loading={submitting}>
          {formCta}
        </CTA>
        {submitError && (
          <ResponsiveText variant="body-2" as="p" color="charcoal-3">
            Our server was a bad boy. Please try again or{' '}
            <Link href={`${websiteUrl}${paths.PATH_SIGNUP_ME}`}>
              continue on our website
            </Link>
            .
          </ResponsiveText>
        )}
      </form>
    </Box>
  );
}
